import { z } from 'zod';
import {
  ClassActiveSchema,
  ClassAllowMakeupsSchema,
  ClassCodeSchema,
  ClassCourseIdSchema,
  ClassDaySchema,
  ClassIdSchema,
  ClassLabelSchema,
  ClassLocationSchema,
  ClassMarkerIdSchema,
  ClassMarkerNameSchema,
  ClassNotesSchema,
  ClassPermissionGroupIdSchema,
  ClassRemindBreaksSchema,
  ClassRoomIdSchema,
  ClassSchoolTagSchema,
  ClassSlackChannelIdSchema,
  ClassSlackChannelNameSchema,
  ClassStreamIdSchema,
  ClassStudentCapSchema,
  ClassStudentCountSchema,
  ClassStudentsSchema,
  ClassSubjectSchema,
  ClassTagsSchema,
  ClassTeacherIdSchema,
  ClassTeacherNameSchema,
  ClassTimeSchema,
  ClassWaitlistSchema,
  DBClassClassIdSchema,
  DBClassSchema,
} from '../classes/schemas';
import { SubjectSchema, UnixTimestampSchema } from '../common/schemas';
import {
  DBLessonEnrolmentSchema,
  LessonEnrolmentAttendanceNotifiedSchema,
  LessonEnrolmentAttendanceTypeSchema,
  LessonEnrolmentBillingTypeSchema,
  LessonEnrolmentClassCodeSchema,
  LessonEnrolmentClassIdSchema,
  LessonEnrolmentClassLabelSchema,
  LessonEnrolmentCounterSchema,
  LessonEnrolmentCourseIdSchema,
  LessonEnrolmentEnrolmentMethodSchema,
  LessonEnrolmentHomeworkSchema,
  LessonEnrolmentIdSchema,
  LessonEnrolmentLessonIdSchema,
  LessonEnrolmentLessonTemplateCodeSchema,
  LessonEnrolmentPresentSchema,
  LessonEnrolmentRollMarkedSchema,
  LessonEnrolmentSeparatelyBilledSchema,
  LessonEnrolmentStartDatetimeSchema,
  LessonEnrolmentStreamIdSchema,
  LessonEnrolmentStudentIdSchema,
  LessonEnrolmentSubjectSchema,
} from '../lessonEnrolments/schemas';
import { DBStaffStaffIdSchema } from '../staff/schemas';
import {
  DBStudentCourseStateStateSchema,
  DBStudentStateSchema,
  DBStudentStudentIdSchema,
} from '../students/schemas';
import { StudentActivityReasonCategory, StudentActivityType, UnenrolmentType } from './enums';

/**
 * Shared & Helper Schemas
 */

export const DBStudentActivityLogUnenrolmentTypeSchema = z.enum(UnenrolmentType);

/**
 * DB Schemas
 */

export const DBStudentActivityLogStudentSchema = z.lazy(() => DBStudentStudentIdSchema);
export const DBStudentActivityLogTypeDatetimeIdentifierSchema = z.string().refine(val => {
  const parts = val.split('#');
  return (
    parts.length === 2 &&
    z.enum(StudentActivityType).safeParse(parts[0]).success &&
    UnixTimestampSchema.safeParse(Number(parts[1])).success
  );
});
export const DBStudentActivityLogActorSchema = z.lazy(() => DBStaffStaffIdSchema);
export const DBStudentActivityLogAttendedLessonsCountSchema = z.number().int().min(0);
export const DBStudentActivityLogClassSchema = z.lazy(() => DBClassSchema);
export const DBStudentActivityLogClassIdSchema = z.lazy(() => DBClassClassIdSchema);
export const DBStudentActivityLogClassSizeSchema = z.number().int().min(0);
export const DBStudentActivityLogCourseStateSchema = z.lazy(() => DBStudentCourseStateStateSchema);
export const DBStudentActivityLogCreationDatetimeSchema = UnixTimestampSchema;
export const DBStudentActivityLogReasonSchema = z.string().min(0).max(4096);
export const DBStudentActivityLogReasonCategorySchema = z.enum(StudentActivityReasonCategory);
export const DBStudentActivityLogRecentAttendedLessonsSchema = z.lazy(() =>
  z.array(DBLessonEnrolmentSchema)
);
export const DBStudentActivityLogRecentAttendedTeachersSchema = z.array(
  z.lazy(() => DBStaffStaffIdSchema)
);
export const DBStudentActivityLogStudentStateSchema = z.lazy(() => DBStudentStateSchema);
export const DBStudentActivityLogSubjectSchema = SubjectSchema;
// Diverges from DBClassTeacherSchema (which accepts UUID | '') because the
// studentActivityLog table uses `teacher` as a GSI partition key on the
// `byTeacherTypeDatetime` index, and DynamoDB forbids empty strings as GSI
// PK values. The codec stores '_' for an unassigned teacher so the GSI
// partition is always non-empty.
export const DBStudentActivityLogTeacherSchema = z.union([
  z.lazy(() => DBStaffStaffIdSchema),
  z.literal('_'),
]);
export const DBStudentActivityLogTotalLessonsCountSchema = z.number().int().min(0);

export const DBStudentActivityLogSchema = z.object({
  student: DBStudentActivityLogStudentSchema,
  typeDatetimeIdentifier: DBStudentActivityLogTypeDatetimeIdentifierSchema,
  actor: DBStudentActivityLogActorSchema,
  creationDatetime: DBStudentActivityLogCreationDatetimeSchema,
  // Legacy records pre-date these fields being written on every log entry.
  class: DBStudentActivityLogClassSchema.optional(),
  classId: DBStudentActivityLogClassIdSchema.optional(),
  classSize: DBStudentActivityLogClassSizeSchema.optional(),
  studentState: DBStudentActivityLogStudentStateSchema.optional(),
  subject: DBStudentActivityLogSubjectSchema.optional(),
  teacher: DBStudentActivityLogTeacherSchema.optional(),
  // Optional fields - only present for certain activity types
  courseState: DBStudentActivityLogCourseStateSchema.optional(),
  reason: DBStudentActivityLogReasonSchema.optional(),
  reasonCategory: DBStudentActivityLogReasonCategorySchema.optional(),
  attendedLessonsCount: DBStudentActivityLogAttendedLessonsCountSchema.optional(),
  totalLessonsCount: DBStudentActivityLogTotalLessonsCountSchema.optional(),
  recentAttendedLessons: DBStudentActivityLogRecentAttendedLessonsSchema.optional(),
  recentAttendedTeachers: DBStudentActivityLogRecentAttendedTeachersSchema.optional(),
});

/**
 * Client Schemas
 */

export const StudentActivityLogIdSchema = z.string().brand<'ActivityLogEntryId'>();
export const StudentActivityLogStudentIdSchema = DBStudentActivityLogStudentSchema;
export const StudentActivityLogActivityTypeSchema = z.enum(StudentActivityType);
export const StudentActivityLogCreatedBySchema = DBStudentActivityLogActorSchema;
export const StudentActivityLogAttendedLessonsCountSchema =
  DBStudentActivityLogAttendedLessonsCountSchema;
export const StudentActivityLogClassSchema = z.lazy(() =>
  z.object({
    id: z.lazy(() => ClassIdSchema),
    active: z.lazy(() => ClassActiveSchema),
    allowMakeups: z.lazy(() => ClassAllowMakeupsSchema),
    code: z.lazy(() => ClassCodeSchema),
    courseId: z.lazy(() => ClassCourseIdSchema),
    day: z.lazy(() => ClassDaySchema).nullish(),
    label: z.lazy(() => ClassLabelSchema),
    location: z.lazy(() => ClassLocationSchema),
    markerId: z.lazy(() => ClassMarkerIdSchema).nullish(),
    markerName: z.lazy(() => ClassMarkerNameSchema).nullish(),
    notes: z.lazy(() => ClassNotesSchema),
    permissionGroupId: z.lazy(() => ClassPermissionGroupIdSchema),
    remindBreaks: z.lazy(() => ClassRemindBreaksSchema),
    roomId: z.lazy(() => ClassRoomIdSchema),
    schoolTag: z.lazy(() => ClassSchoolTagSchema),
    slackChannelName: z.lazy(() => ClassSlackChannelNameSchema),
    slackChannelId: z.lazy(() => ClassSlackChannelIdSchema),
    streamId: z.lazy(() => ClassStreamIdSchema),
    studentCap: z.lazy(() => ClassStudentCapSchema),
    studentCount: z.lazy(() => ClassStudentCountSchema),
    students: z.lazy(() => ClassStudentsSchema),
    subject: z.lazy(() => ClassSubjectSchema),
    tags: z.lazy(() => ClassTagsSchema),
    teacherId: z.lazy(() => ClassTeacherIdSchema).nullish(),
    teacherName: z.lazy(() => ClassTeacherNameSchema).nullish(),
    time: z.lazy(() => ClassTimeSchema),
    waitlist: z.lazy(() => ClassWaitlistSchema),
  })
);
export const StudentActivityLogClassIdSchema = z.lazy(() => ClassIdSchema);
export const StudentActivityLogClassCodeSchema = DBStudentActivityLogClassIdSchema;
export const StudentActivityLogClassSizeSchema = DBStudentActivityLogClassSizeSchema;
export const StudentActivityLogCourseStateSchema = DBStudentActivityLogCourseStateSchema;
export const StudentActivityLogCreationDatetimeSchema = DBStudentActivityLogCreationDatetimeSchema;
export const StudentActivityLogReasonSchema = DBStudentActivityLogReasonSchema;
export const StudentActivityLogReasonCategorySchema = DBStudentActivityLogReasonCategorySchema;
export const StudentActivityLogRecentAttendedLessonsSchema = z.lazy(() =>
  z.array(
    z.object({
      id: z.lazy(() => LessonEnrolmentIdSchema),
      studentId: z.lazy(() => LessonEnrolmentStudentIdSchema),
      lessonId: z.lazy(() => LessonEnrolmentLessonIdSchema),
      classId: z.lazy(() => LessonEnrolmentClassIdSchema),
      subject: z.lazy(() => LessonEnrolmentSubjectSchema),
      classCode: z.lazy(() => LessonEnrolmentClassCodeSchema),
      courseId: z.lazy(() => LessonEnrolmentCourseIdSchema),
      lessonTemplateCode: z.lazy(() => LessonEnrolmentLessonTemplateCodeSchema),
      counter: z.lazy(() => LessonEnrolmentCounterSchema),
      classLabel: z.lazy(() => LessonEnrolmentClassLabelSchema),
      attendanceNotified: z.lazy(() => LessonEnrolmentAttendanceNotifiedSchema),
      attendanceType: z.lazy(() => LessonEnrolmentAttendanceTypeSchema),
      billingType: z.lazy(() => LessonEnrolmentBillingTypeSchema),
      enrolmentMethod: z.lazy(() => LessonEnrolmentEnrolmentMethodSchema),
      homework: z.lazy(() => LessonEnrolmentHomeworkSchema),
      present: z.lazy(() => LessonEnrolmentPresentSchema),
      rollMarked: z.lazy(() => LessonEnrolmentRollMarkedSchema),
      separatelyBilled: z.lazy(() => LessonEnrolmentSeparatelyBilledSchema),
      startDatetime: z.lazy(() => LessonEnrolmentStartDatetimeSchema),
      streamId: z.lazy(() => LessonEnrolmentStreamIdSchema).optional(),
    })
  )
);
export const StudentActivityLogRecentAttendedTeacherIdsSchema =
  DBStudentActivityLogRecentAttendedTeachersSchema;
export const StudentActivityLogStudentStateSchema = DBStudentActivityLogStudentStateSchema;
export const StudentActivityLogSubjectSchema = DBStudentActivityLogSubjectSchema;
export const StudentActivityLogTeacherIdSchema = z.lazy(() => DBStaffStaffIdSchema);
export const StudentActivityLogTotalLessonsCountSchema =
  DBStudentActivityLogTotalLessonsCountSchema;

export const StudentActivityLogSchema = z.object({
  id: StudentActivityLogIdSchema,
  studentId: StudentActivityLogStudentIdSchema.meta({ title: 'Student ID' }),
  activityType: StudentActivityLogActivityTypeSchema.meta({ title: 'Activity type' }),
  createdBy: StudentActivityLogCreatedBySchema.meta({ title: 'Actor' }),
  creationDatetime: StudentActivityLogCreationDatetimeSchema.meta({ title: 'Creation date' }),
  // Legacy records pre-date these fields being written on every log entry.
  class: StudentActivityLogClassSchema.optional().meta({ title: 'Class' }),
  classId: StudentActivityLogClassIdSchema.optional(),
  classCode: StudentActivityLogClassCodeSchema.optional().meta({ title: 'Class code' }),
  classSize: StudentActivityLogClassSizeSchema.optional().meta({ title: 'Class size' }),
  studentState: StudentActivityLogStudentStateSchema.optional().meta({ title: 'Student state' }),
  subject: StudentActivityLogSubjectSchema.optional().meta({ title: 'Subject' }),
  // Absent while unassigned
  teacherId: StudentActivityLogTeacherIdSchema.optional().meta({ title: 'Teacher ID' }),
  // Optional fields - only present for certain activity types
  courseState: StudentActivityLogCourseStateSchema.optional().meta({ title: 'Course state' }),
  reason: StudentActivityLogReasonSchema.optional().meta({ title: 'Reason' }),
  reasonCategory: StudentActivityLogReasonCategorySchema.optional().meta({
    title: 'Reason category',
  }),
  attendedLessonsCount: StudentActivityLogAttendedLessonsCountSchema.optional().meta({
    title: 'Attended lessons count',
  }),
  totalLessonsCount: StudentActivityLogTotalLessonsCountSchema.optional().meta({
    title: 'Total lessons count',
  }),
  recentAttendedLessons: StudentActivityLogRecentAttendedLessonsSchema.optional().meta({
    title: 'Recent lessons',
  }),
  recentAttendedTeacherIds: StudentActivityLogRecentAttendedTeacherIdsSchema.optional().meta({
    title: 'Recent teachers',
  }),
});
